/**
 * Maps an AI {@link CompanyPrefill} payload onto the wizard's Company form
 * state + per-field "AI suggested" confidence map.
 *
 * Shared by two write paths so they can't drift on field shape:
 *   - the initial `/prefill` apply (domain-lane enrichment), and
 *   - the generic-domain `/fallback-resolve` apply (Onboarding #8).
 *
 * Only fields the AI actually returned a value for are included — empty fields
 * are left for the user to fill (and don't get a confidence badge).
 */
import type { CompanyPrefill } from '../../../shared/api/setupWizardApi';
import type { CompanyFormState, CompanyConfidence } from './CompanyStep';
import { normalizeCustomerTypes, normalizeSizeBand } from './options';

export interface MappedCompanyPrefill {
  values: Partial<CompanyFormState>;
  confidence: CompanyConfidence;
}

/**
 * How much of Step 1 the AI actually answered. Drives the step's copy: "Confirm
 * your company / Looks good" only earns its wording when the core identity
 * fields came back filled — otherwise the user is authoring, not confirming.
 *
 *  - `pending`  — the poll is still running.
 *  - `enriched` — name plus at least one business signal (website/industry).
 *  - `thin`     — something came back, but not enough to call it a confirmation.
 *  - `none`     — enrichment failed, timed out, or returned an empty company.
 */
export type PrefillOutcome = 'pending' | 'enriched' | 'thin' | 'none';

/**
 * `website` is echoed back from the domain the backend *attempted* to enrich
 * (`https://{domain}`), so it is present even when the homepage was unreachable
 * and every real field came back null. It therefore proves nothing on its own —
 * only the other fields count as evidence the AI learned something.
 */
function learnedAnything(values: Partial<CompanyFormState>): boolean {
  return Object.keys(values).some((field) => field !== 'website');
}

export function classifyPrefill(values: Partial<CompanyFormState>): PrefillOutcome {
  if (!learnedAnything(values)) return 'none';
  if (values.name && (values.website || values.industry)) return 'enriched';
  return 'thin';
}

export function mapCompanyPrefill(c: CompanyPrefill): MappedCompanyPrefill {
  const values: Partial<CompanyFormState> = {};
  const confidence: CompanyConfidence = {};

  if (c.name.value) {
    values.name = c.name.value;
    confidence.name = c.name.confidence;
  }
  if (c.website.value) {
    values.website = c.website.value;
    confidence.website = c.website.confidence;
  }
  if (c.primary_country.value) {
    values.country = c.primary_country.value;
    confidence.country = c.primary_country.confidence;
  }
  if (c.industry.value) {
    values.industry = c.industry.value;
    confidence.industry = c.industry.confidence;
  }
  if (c.customer_types.value) {
    const types = normalizeCustomerTypes(c.customer_types.value);
    if (types.length > 0) {
      values.customerTypes = types;
      confidence.customerTypes = c.customer_types.confidence;
    }
  }
  if (c.size_band.value) {
    const band = normalizeSizeBand(c.size_band.value);
    if (band) {
      values.sizeBand = band;
      confidence.sizeBand = c.size_band.confidence;
    }
  }
  if (c.description.value) {
    values.description = c.description.value;
    confidence.description = c.description.confidence;
  }
  if (c.brand_color) values.brandColor = c.brand_color;
  if (c.logo_file_id) values.logoFileId = c.logo_file_id;

  return { values, confidence };
}
